<template>
    <DropdownButton center-on-mobile>
        <template #trigger>
            <button class="button is-primary">
                <span class="icon">
                    <i class="fas fa-plus"></i>
                </span>
                <span>{{ translate("page.category_manager.add_categories_button") }}</span>
                <span class="icon">
                    <i class="fas fa-angle-down"></i>
                </span>
            </button>
        </template>
        <template #content>
            <div class="dropdown-item is-append-menu-header">
                {{ translate("page.category_manager.append_menu_header") }}
            </div>

            <a v-for="item in visibleMenuItems" :key="item.key" class="dropdown-item append-menu-item" @click="onSelect(item.key)">
                <span class="icon append-menu-item__icon" :class="item.iconColor">
                    <i class="fas" :class="item.icon"></i>
                </span>
                <span class="append-menu-item__body">
                    <span class="append-menu-item__title">
                        {{ translate(item.titleKey) }}
                        <span v-if="item.badge" class="tag is-rounded ml-2" :class="item.badgeClass">
                            {{ translate(item.badge) }}
                        </span>
                    </span>
                    <span class="append-menu-item__description">
                        {{ translate(item.descriptionKey) }}
                    </span>
                </span>
            </a>
        </template>
    </DropdownButton>
</template>

<script setup lang="ts">
    import { computed } from "vue";
    import DropdownButton from "../Common/DropdownButton.vue";
    import { translate } from "../Common/translations";
    import { useCategoriesManagerStore } from "./categoriesManagerStore";

    type MenuItemKey = "template" | "event-rules" | "previous-event";

    interface MenuItem {
        key: MenuItemKey;
        icon: string;
        iconColor: string;
        titleKey: string;
        descriptionKey: string;
        badge?: string;
        badgeClass?: string;
    }

    const emit = defineEmits<{
        select: [key: MenuItemKey];
    }>();

    const menuItems: MenuItem[] = [
        {
            key: "template",
            icon: "fa-box-open",
            iconColor: "has-text-primary",
            titleKey: "page.category_manager.append_from_template_title",
            descriptionKey: "page.category_manager.append_from_template_description",
            badge: "common.recommended",
            badgeClass: "is-info is-light",
        },
        {
            key: "event-rules",
            icon: "fa-file-alt",
            iconColor: "has-text-info",
            titleKey: "page.category_manager.append_from_rules_title",
            descriptionKey: "page.category_manager.path_extract_rules_description",
            badge: "common.beta",
            badgeClass: "is-warning is-light",
        },
        {
            key: "previous-event",
            icon: "fa-clone",
            iconColor: "has-text-success",
            titleKey: "page.category_manager.append_from_event_title",
            descriptionKey: "page.category_manager.append_from_event_description",
        },
    ];

    const store = useCategoriesManagerStore();

    // "Import from event rules" (AI extraction) is a full-organizer-only capability, matching the
    // gating on the four-path setup screen and the extraction API.
    const visibleMenuItems = computed(() => menuItems.filter(item => item.key !== "event-rules" || store.isFullOrganizer));

    function onSelect(key: MenuItemKey) {
        emit("select", key);
    }
</script>

<style scoped>
    @media screen and (min-width: 769px) {
        :deep(.dropdown-menu) {
            min-width: 24rem;
        }
    }

    .dropdown-item.is-append-menu-header {
        text-transform: uppercase;
        letter-spacing: 0.08em;
        font-size: 0.7rem;
        font-weight: 700;
        color: var(--bulma-text-weak, #8a9ba8);
    }

    .append-menu-item,
    .append-menu-item:hover {
        text-decoration: none;
    }

    .append-menu-item {
        display: flex;
        align-items: flex-start;
        gap: 0.75rem;
        padding-top: 0.6rem;
        padding-bottom: 0.6rem;
        white-space: normal;
    }

    .append-menu-item__icon {
        margin-top: 0.15rem;
    }

    .append-menu-item__body {
        display: flex;
        flex-direction: column;
        gap: 0.15rem;
    }

    .append-menu-item__title {
        font-weight: 600;
    }

    .append-menu-item__description {
        font-size: 0.8rem;
        color: var(--bulma-text-weak, #8a9ba8);
    }
</style>
